// src/components/resources/tools/generators/shared/useStepScrollReset.ts

import { useEffect, useRef } from 'react'

interface StepScrollOptions {
  /**
   * Optional scroll offset that keeps sticky headers (like the generator nav)
   * from covering the top of the step container after navigation.
   */
  offset?: number
  /**
   * Allows consumers to request instant jumps (desktop) or smooth scrolling (mobile).
   */
  behavior?: ScrollBehavior
}

/**
 * Resets the scroll position to the top of the step container whenever the active
 * step index changes. Mobile users often advance the wizard while scrolled to the
 * bottom, so without this hook they land halfway through the next screen.
 */
export function useStepScrollReset<T extends HTMLElement>(
  stepIndex: number,
  { offset = 112, behavior = 'smooth' }: StepScrollOptions = {}
): React.MutableRefObject<T | null> {
  const containerRef = useRef<T | null>(null)
  const previousStepRef = useRef(stepIndex)

  useEffect(() => {
    const container = containerRef.current

    if (!container) {
      previousStepRef.current = stepIndex
      return
    }

    if (previousStepRef.current === stepIndex) {
      return
    }

    previousStepRef.current = stepIndex

    // Wait for the DOM to paint the next step before measuring and scrolling.
    requestAnimationFrame(() => {
      const rect = container.getBoundingClientRect()
      const absoluteTop = rect.top + window.scrollY

      // Guard against negative offsets on tiny screens.
      const target = Math.max(0, absoluteTop - offset)

      // Using window scrolling keeps the behavior consistent even when the
      // parent layout is not the scroll container (e.g. GeneratorLayout).
      window.scrollTo({ top: target, behavior })
    })
  }, [stepIndex, offset, behavior])

  return containerRef
}

